// חלקי מסך קמפיין: בריף, טקסטים נעולים, חריגות שפה גרפית, חומרים.
import { useMemo, useState } from 'react';
import type { BrandKit } from '@bb/schema';
import { api } from '../../api';
import { Empty, ErrorBox, Field, Loading, useLoad } from '../ui';
import { CAMPAIGN_STATUS, Check, HEX_RE, LANGS, confirmAction, useAction } from './common';
import { isUrl } from './LinksEditor';
import { AssetPicker, AssetThumb, UploadModal, useClientAssets, ASSET_KINDS } from './AssetLibrary';
import { COLOR_ROLES, FONT_ROLES, normalizeKit } from './BrandKitEditor';

export interface CopyItem { key: string; text: string; locked: boolean }
export interface CampaignCopy { headlines: CopyItem[]; texts: CopyItem[]; ctas: CopyItem[]; disclaimer: { text: string; locked: boolean } | null }
export interface CampaignRow {
  id: string; client_id: string; brand_id: string; brand_version_id: string; name: string; campaign_key: string; description: string; goal: string; language: string;
  start_date: string | null; end_date: string | null; status: string; copy: CampaignCopy; default_url: string; overrides: { colors?: BrandKit['colors']; fonts?: BrandKit['fonts'] }; local_kit: BrandKit | null; updated_at: string;
}
export interface CampaignFull {
  campaign: CampaignRow; kit: BrandKit; overridden: string[]; brandVersion: { id: string; version: number }; usesLocalKit: boolean; newerBrandVersion: string | null;
  sets: { id: string; name: string; status: string; variant_count: number; created_at: string; updated_at: string }[];
  assets: { id: string }[];
}

export function toDateInput(s: string | null | undefined): string {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(+d)) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── בריף ──
export function BriefForm({ c, canEdit, onSaved }: { c: CampaignRow; canEdit: boolean; onSaved: (r: CampaignFull) => void }) {
  const [f, setF] = useState({ name: c.name, description: c.description, goal: c.goal, language: c.language, startDate: toDateInput(c.start_date), endDate: toDateInput(c.end_date), status: c.status, defaultUrl: c.default_url });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const { busy, error, run } = useAction();
  const urlBad = f.defaultUrl !== '' && !isUrl(f.defaultUrl);
  const datesBad = !!(f.startDate && f.endDate && f.endDate < f.startDate);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await run(() => api.patch<CampaignFull>(`/api/campaigns/${c.id}`, { ...f, startDate: f.startDate || null, endDate: f.endDate || null }), 'הבריף נשמר');
    if (r) onSaved(r);
  };
  return (
    <form className="stack" onSubmit={submit}>
      <fieldset disabled={!canEdit} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }} className="stack">
        <div className="grid cols-3">
          <Field label="שם הקמפיין"><input required value={f.name} onChange={(e) => set({ name: e.target.value })} /></Field>
          <Field label="מפתח קמפיין" hint="קבוע — משמש ב-UTM"><input className="ltr mono" value={c.campaign_key} readOnly /></Field>
          <Field label="סטטוס"><select value={f.status} onChange={(e) => set({ status: e.target.value })}>{Object.entries(CAMPAIGN_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="שפה"><select value={f.language} onChange={(e) => set({ language: e.target.value })}>{Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="תאריך התחלה"><input type="date" value={f.startDate} onChange={(e) => set({ startDate: e.target.value })} /></Field>
          <Field label="תאריך סיום"><input type="date" value={f.endDate} onChange={(e) => set({ endDate: e.target.value })} aria-invalid={datesBad} /></Field>
        </div>
        {datesBad && <div className="small" style={{ color: 'var(--danger)' }}>תאריך הסיום לפני תאריך ההתחלה</div>}
        <Field label="URL ברירת מחדל" hint="כל וריאציה יורשת את הכתובת וניתן לדרוס אותה בטבלת הקישורים"><input className="ltr" value={f.defaultUrl} onChange={(e) => set({ defaultUrl: e.target.value })} placeholder="https://" aria-invalid={urlBad} /></Field>
        {urlBad && <div className="small" style={{ color: 'var(--danger)' }}>כתובת לא תקינה</div>}
        <Field label="מטרה"><textarea value={f.goal} onChange={(e) => set({ goal: e.target.value })} style={{ minHeight: 60 }} /></Field>
        <Field label="תיאור / בריף"><textarea value={f.description} onChange={(e) => set({ description: e.target.value })} style={{ minHeight: 120 }} /></Field>
      </fieldset>
      <ErrorBox error={error} />
      {canEdit && <div><button className="btn primary" disabled={busy || urlBad || datesBad}>{busy ? 'שומר…' : 'שמירת בריף'}</button></div>}
    </form>
  );
}

// ── טקסטים ──
const COPY_KEY_RE = /^[a-z0-9_-]{1,40}$/;
const COPY_GROUPS: { k: 'headlines' | 'texts' | 'ctas'; label: string; max: number; maxLen: number; prefix: string }[] = [
  { k: 'headlines', label: 'כותרות', max: 30, maxLen: 300, prefix: 'headline' },
  { k: 'texts', label: 'טקסטים', max: 30, maxLen: 500, prefix: 'text' },
  { k: 'ctas', label: 'CTA', max: 10, maxLen: 60, prefix: 'cta' },
];

export function CopyEditor({ c, canEdit, onSaved }: { c: CampaignRow; canEdit: boolean; onSaved: (r: CampaignFull) => void }) {
  const [copy, setCopy] = useState<CampaignCopy>(() => ({ ...({ headlines: [], texts: [], ctas: [], disclaimer: null } as CampaignCopy), ...((c.copy ?? {}) as Partial<CampaignCopy>) }));
  const { busy, error, run } = useAction();
  const dupKeys = (items: CopyItem[]) => items.map((i) => i.key).filter((k, i, a) => a.indexOf(k) !== i);
  const invalid = COPY_GROUPS.some((g) => copy[g.k].some((i) => !COPY_KEY_RE.test(i.key)) || dupKeys(copy[g.k]).length > 0);
  const setItem = (g: 'headlines' | 'texts' | 'ctas', i: number, p: Partial<CopyItem>) => setCopy((x) => ({ ...x, [g]: x[g].map((it, j) => (j === i ? { ...it, ...p } : it)) }));
  const add = (g: typeof COPY_GROUPS[number]) => setCopy((x) => {
    let n = x[g.k].length + 1;
    while (x[g.k].some((i) => i.key === `${g.prefix}${n}`)) n++;
    return { ...x, [g.k]: [...x[g.k], { key: `${g.prefix}${n}`, text: '', locked: false }] };
  });
  const save = async () => {
    const r = await run(() => api.patch<CampaignFull>(`/api/campaigns/${c.id}`, { copy }), 'הטקסטים נשמרו');
    if (r) onSaved(r);
  };
  return (
    <div className="stack">
      <div className="alert info small">טקסט <strong>נעול</strong> נשמר כלשונו: המודל אינו רשאי לערוך, לקצר או לתרגם אותו. מפתח (key) הוא מזהה לטיני קבוע לטקסט.</div>
      {COPY_GROUPS.map((g) => (
        <fieldset className="box" key={g.k} disabled={!canEdit}>
          <legend>{g.label} ({copy[g.k].length}/{g.max})</legend>
          {copy[g.k].length === 0 && <div className="small muted">אין פריטים.</div>}
          {copy[g.k].map((it, i) => {
            const keyBad = !COPY_KEY_RE.test(it.key) || dupKeys(copy[g.k]).includes(it.key);
            return (
              <div className="row" key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                <input aria-label={`${g.label} ${i + 1} — מפתח`} className="ltr mono" value={it.key} onChange={(e) => setItem(g.k, i, { key: e.target.value })} style={{ width: 120, borderColor: keyBad ? 'var(--danger)' : undefined }} aria-invalid={keyBad} />
                {g.k === 'texts'
                  ? <textarea aria-label={`${g.label} ${i + 1} — טקסט`} value={it.text} maxLength={g.maxLen} onChange={(e) => setItem(g.k, i, { text: e.target.value })} style={{ flex: '1 1 220px', minHeight: 50 }} />
                  : <input aria-label={`${g.label} ${i + 1} — טקסט`} value={it.text} maxLength={g.maxLen} onChange={(e) => setItem(g.k, i, { text: e.target.value })} style={{ flex: '1 1 220px' }} />}
                <Check checked={it.locked} onChange={(v) => setItem(g.k, i, { locked: v })}>נעול {it.locked && <span className="badge warn">המודל לא יערוך</span>}</Check>
                <button type="button" className="btn small danger" onClick={() => setCopy((x) => ({ ...x, [g.k]: x[g.k].filter((_, j) => j !== i) }))} aria-label={`הסרת ${g.label} ${i + 1}`}>הסרה</button>
              </div>
            );
          })}
          <button type="button" className="btn small" disabled={copy[g.k].length >= g.max} onClick={() => add(g)} style={{ marginTop: 6 }}>הוספה</button>
        </fieldset>
      ))}
      <fieldset className="box" disabled={!canEdit}>
        <legend>גילוי נאות</legend>
        {copy.disclaimer ? (
          <div className="stack">
            <textarea aria-label="טקסט גילוי נאות" value={copy.disclaimer.text} maxLength={500} onChange={(e) => setCopy((x) => ({ ...x, disclaimer: { ...x.disclaimer!, text: e.target.value } }))} />
            <div className="row">
              <Check checked={copy.disclaimer.locked} onChange={(v) => setCopy((x) => ({ ...x, disclaimer: { ...x.disclaimer!, locked: v } }))}>נעול (מומלץ)</Check>
              <button type="button" className="btn small danger" onClick={() => setCopy((x) => ({ ...x, disclaimer: null }))}>הסרת גילוי נאות</button>
            </div>
          </div>
        ) : <button type="button" className="btn small" onClick={() => setCopy((x) => ({ ...x, disclaimer: { text: '', locked: true } }))}>הוספת גילוי נאות</button>}
      </fieldset>
      {invalid && <div className="alert err small">יש מפתחות לא תקינים או כפולים (a-z, 0-9, - ו-_ בלבד, עד 40 תווים).</div>}
      <ErrorBox error={error} />
      {canEdit && <div><button className="btn primary" onClick={save} disabled={busy || invalid}>{busy ? 'שומר…' : 'שמירת טקסטים'}</button></div>}
    </div>
  );
}

// ── חריגות שפה גרפית ──
export function OverridesEditor({ data, canEdit, onSaved }: { data: CampaignFull; canEdit: boolean; onSaved: (r: CampaignFull) => void }) {
  const c = data.campaign;
  const { data: bv, loading, error: bvErr } = useLoad<{ kit: BrandKit; version: number }>(`/api/brand-versions/${data.brandVersion.id}`);
  const base = useMemo(() => normalizeKit(c.local_kit ?? bv?.kit), [c.local_kit, bv]);
  const [ov, setOv] = useState<CampaignRow['overrides']>(() => structuredClone(c.overrides ?? {}));
  const { busy, error, run } = useAction();
  const save = async () => {
    const out: CampaignRow['overrides'] = {};
    if (ov.colors) out.colors = ov.colors;
    if (ov.fonts) out.fonts = ov.fonts;
    const r = await run(() => api.patch<CampaignFull>(`/api/campaigns/${c.id}`, { overrides: out }), 'החריגות נשמרו');
    if (r) onSaved(r);
  };
  if (loading && !bv) return <Loading />;
  const badHex = (ov.colors ?? []).some((x) => !HEX_RE.test(x.hex));
  return (
    <div className="stack">
      <ErrorBox error={bvErr} />
      <div className="small muted">בסיס: {data.usesLocalKit ? 'שפה מקומית של הקמפיין' : `מותג גרסה ${data.brandVersion.version}`}. חריגה ברמת הקמפיין אינה משנה את המותג, ומסומנת בכל מקום.</div>
      <section className={ov.colors ? 'override-flag' : ''} aria-label="צבעים">
        <div className="row between"><h3>צבעים {ov.colors && <span className="badge warn">חריגה מהמותג</span>}</h3>
          {canEdit && (ov.colors
            ? <button className="btn small" onClick={() => { if (confirmAction('לבטל את חריגת הצבעים ולחזור לצבעי המותג?')) setOv((x) => ({ ...x, colors: undefined })); }}>חזרה לצבעי המותג</button>
            : <button className="btn small" onClick={() => setOv((x) => ({ ...x, colors: structuredClone(base.colors) }))}>חריגה בצבעים</button>)}
        </div>
        {!ov.colors && <div className="row">{base.colors.length ? base.colors.map((x, i) => <span key={i} className="row" style={{ gap: 4 }}><span className="swatch" style={{ background: x.hex }} /><span className="mono">{x.hex}</span><span className="small muted">{COLOR_ROLES[x.role]}</span></span>) : <span className="small muted">אין צבעים במותג.</span>}</div>}
        {ov.colors && (
          <div>
            {ov.colors.map((x, i) => {
              const orig = base.colors.find((b) => b.role === x.role);
              const changed = !orig || orig.hex.toLowerCase() !== x.hex.toLowerCase();
              return (
                <div className="color-row" key={i}>
                  <input type="color" aria-label={`צבע ${i + 1}`} value={HEX_RE.test(x.hex) ? x.hex : '#000000'} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, colors: o.colors!.map((y, j) => (j === i ? { ...y, hex: e.target.value } : y)) }))} />
                  <input className="hex mono" aria-label={`HEX ${i + 1}`} value={x.hex} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, colors: o.colors!.map((y, j) => (j === i ? { ...y, hex: e.target.value.trim() } : y)) }))} />
                  <select aria-label={`תפקיד ${i + 1}`} value={x.role} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, colors: o.colors!.map((y, j) => (j === i ? { ...y, role: e.target.value as typeof y.role } : y)) }))}>
                    {Object.entries(COLOR_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {changed && <span className="badge warn">שונה מהמותג{orig ? ` (${orig.hex})` : ''}</span>}
                  {canEdit && <button className="btn small danger" onClick={() => setOv((o) => ({ ...o, colors: o.colors!.filter((_, j) => j !== i) }))}>הסרה</button>}
                </div>
              );
            })}
            {canEdit && <button className="btn small" onClick={() => setOv((o) => ({ ...o, colors: [...o.colors!, { hex: '#000000', role: 'other' }] }))}>הוספת צבע</button>}
            {badHex && <div className="small" style={{ color: 'var(--danger)' }}>HEX בפורמט #RRGGBB</div>}
          </div>
        )}
      </section>
      <section className={ov.fonts ? 'override-flag' : ''} aria-label="פונטים">
        <div className="row between"><h3>פונטים {ov.fonts && <span className="badge warn">חריגה מהמותג</span>}</h3>
          {canEdit && (ov.fonts
            ? <button className="btn small" onClick={() => { if (confirmAction('לבטל את חריגת הפונטים?')) setOv((x) => ({ ...x, fonts: undefined })); }}>חזרה לפונטי המותג</button>
            : <button className="btn small" onClick={() => setOv((x) => ({ ...x, fonts: structuredClone(base.fonts) }))}>חריגה בפונטים</button>)}
        </div>
        {!ov.fonts && <div className="small">{base.fonts.length ? base.fonts.map((f) => `${f.family} (${FONT_ROLES[f.role]})`).join(' · ') : <span className="muted">אין פונטים במותג.</span>}</div>}
        {ov.fonts && (
          <div className="stack">
            {ov.fonts.map((f, i) => (
              <div className="row" key={i}>
                <input aria-label={`משפחת פונט ${i + 1}`} className="ltr" value={f.family} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, fonts: o.fonts!.map((y, j) => (j === i ? { ...y, family: e.target.value } : y)) }))} />
                <input aria-label={`משקלים ${i + 1}`} className="ltr" style={{ width: 100 }} value={f.weights.join(',')} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, fonts: o.fonts!.map((y, j) => (j === i ? { ...y, weights: e.target.value.split(',').map((w) => parseInt(w, 10)).filter(Number.isFinite) } : y)) }))} />
                <select aria-label={`תפקיד פונט ${i + 1}`} value={f.role} disabled={!canEdit} onChange={(e) => setOv((o) => ({ ...o, fonts: o.fonts!.map((y, j) => (j === i ? { ...y, role: e.target.value as typeof y.role } : y)) }))}>
                  {Object.entries(FONT_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {!f.assetId && <span className="badge">שם בלבד — לא יוטמע</span>}
                {canEdit && <button className="btn small danger" onClick={() => setOv((o) => ({ ...o, fonts: o.fonts!.filter((_, j) => j !== i) }))}>הסרה</button>}
              </div>
            ))}
            <div className="small muted">פונט מוטמע רק עם קובץ ורישיון הטמעה. קובץ פונט בוחרים בערכת המותג.</div>
          </div>
        )}
      </section>
      <ErrorBox error={error} />
      {canEdit && <div><button className="btn primary" onClick={save} disabled={busy || badHex}>{busy ? 'שומר…' : 'שמירת חריגות'}</button></div>}
    </div>
  );
}

// ── חומרי קמפיין ──
export function CampaignMaterials({ c, canEdit, onSaved }: { c: CampaignRow; canEdit: boolean; onSaved: (r: CampaignFull) => void }) {
  const { data, error, loading, reload } = useClientAssets(c.client_id, { campaignId: c.id });
  const [picking, setPicking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { busy, run } = useAction();
  const link = async (ids: string[], remove = false) => {
    const r = await run(() => api.post<CampaignFull>(`/api/campaigns/${c.id}/assets`, { assetIds: ids, remove }), remove ? 'החומר נותק מהקמפיין' : 'החומרים שויכו לקמפיין');
    if (r) { onSaved(r); reload(); }
  };
  return (
    <div className="stack">
      <div className="row between">
        <span className="small muted">חומרי הלקוח המשויכים לקמפיין זה. שיוך לא מעתיק קבצים; ביטול שיוך לא מוחק את הנכס.</span>
        {canEdit && <div className="row"><button className="btn" onClick={() => setPicking(true)}>שיוך מספריית הלקוח</button><button className="btn primary" onClick={() => setUploading(true)}>העלאה לקמפיין</button></div>}
      </div>
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty>אין חומרים משויכים.</Empty>}
      {data && data.length > 0 && (
        <table className="table cards-sm">
          <thead><tr><th>תצוגה</th><th>שם</th><th>סוג</th><th>תגיות</th><th></th></tr></thead>
          <tbody>{data.map((a) => (
            <tr key={a.id}>
              <td><AssetThumb asset={a} /></td>
              <td data-l="שם">{a.name}</td>
              <td data-l="סוג">{ASSET_KINDS[a.kind] ?? a.kind}</td>
              <td data-l="תגיות" className="small">{a.tags.join(', ')}</td>
              <td>{canEdit && <button className="btn small danger" disabled={busy} onClick={() => { if (confirmAction(`לנתק את "${a.name}" מהקמפיין?`)) link([a.id], true); }}>ניתוק</button>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {picking && <AssetPicker clientId={c.client_id} exclude={(data ?? []).map((a) => a.id)} onClose={() => setPicking(false)} onPick={(ids) => { setPicking(false); link(ids); }} title="שיוך חומרים לקמפיין" />}
      {uploading && <UploadModal clientId={c.client_id} campaignId={c.id} onClose={() => setUploading(false)} onDone={() => reload()} />}
    </div>
  );
}
